import { Entity } from './Entity';
import { System } from './System';
import type { Component } from './Component';

type ComponentClass<T extends Component = Component> = new (...args: unknown[]) => T;

/**
 * The World manages all entities and systems.
 * It's the central hub of the ECS architecture.
 */
export class World {
  private _entities: Entity[] = [];
  private _systems: System[] = [];
  private _entitiesToDestroy: Entity[] = [];

  /**
   * Create a new entity in this world.
   */
  createEntity(): Entity {
    const entity = new Entity();
    entity._setOnDestroy(() => {
      this._entitiesToDestroy.push(entity);
    });
    this._entities.push(entity);
    return entity;
  }

  /**
   * Add a system to this world.
   * Systems are updated in the order they're added.
   */
  addSystem(system: System): this {
    this._systems.push(system);
    system.init();
    return this;
  }

  /**
   * Remove a system from this world.
   */
  removeSystem(system: System): this {
    const index = this._systems.indexOf(system);
    if (index !== -1) {
      system.destroy();
      this._systems.splice(index, 1);
    }
    return this;
  }

  /**
   * Get all entities that have all the specified components.
   */
  query(componentClasses: ComponentClass[]): Entity[] {
    return this._entities.filter((entity) => entity.hasAll(componentClasses));
  }

  /**
   * Update all systems.
   * @param delta - Time since last frame in seconds
   */
  update(delta: number): void {
    // Run all systems
    for (const system of this._systems) {
      const entities = this.query(system.requiredComponents);
      system.update(entities, delta);
    }

    // Clean up destroyed entities
    this._cleanupDestroyedEntities();
  }

  /**
   * Get all entities in this world.
   */
  get entities(): readonly Entity[] {
    return this._entities;
  }

  /**
   * Get all systems in this world.
   */
  get systems(): readonly System[] {
    return this._systems;
  }

  /**
   * Destroy all entities and systems.
   */
  destroy(): void {
    for (const system of this._systems) {
      system.destroy();
    }
    this._systems = [];
    this._entities = [];
    this._entitiesToDestroy = [];
  }

  private _cleanupDestroyedEntities(): void {
    for (const entity of this._entitiesToDestroy) {
      const index = this._entities.indexOf(entity);
      if (index !== -1) {
        this._entities.splice(index, 1);
      }
    }
    this._entitiesToDestroy = [];
  }
}
